import React, { useEffect, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import debounce from 'lodash/debounce';
import LoadingSpinner from '@/Components/LoadingSpinner.jsx';

const DataTableServerSide = ({ columns, routeName, recompute, filters = {}, onDataFetched }) => {
  // Parse initial sorting from filters.sort
  const initialSorting = React.useMemo(() => {
    if (filters?.sort && typeof filters.sort === 'string') {
      return filters.sort.split(',').map((s) => {
        const [id, direction] = s.split(':');
        return {
          id,
          desc: direction === 'desc',
        };
      });
    }
    return [];
  }, [filters?.sort]);

  const [data, setData] = useState([]);
  const [recordCount, setRecordCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState(initialSorting);
  const [globalFilter, setGlobalFilter] = useState(filters?.filter || '');
  const [pagination, setPagination] = useState({
    pageIndex: filters?.page ? Number(filters.page) - 1 : 0,
    pageSize: Math.min(Number(filters?.pageSize) || 10, 100),
  });

  const table = useReactTable({
    data,
    columns,
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.ceil(recordCount / pagination.pageSize),
    state: {
      sorting,
      pagination,
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
    },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function' ? updater(table.getState().pagination) : updater;
      setPagination(newPagination);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const currentPage = pagination.pageIndex + 1;
  const pageSize = pagination.pageSize;
  const totalRows = recordCount;
  const totalPages = Math.ceil(totalRows / pageSize);
  const startRow = (currentPage - 1) * pageSize + 1;
  const endRow = Math.min(currentPage * pageSize, totalRows);

  // Debounced filter function
  const debouncedFilter = React.useMemo(
    () =>
      debounce((newFilter) => {
        setGlobalFilter(newFilter);
        setPagination((prev) => ({ ...prev, pageIndex: 0 })); // Reset to first page on filter change
      }, 300),
    [recompute]
  );

  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    debouncedFilter(newFilter);
  };

  // Function to fetch data from the server
  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const sortString = sorting.map((s) => `${s.id}:${s.desc ? 'desc' : 'asc'}`).join(',');
      const params = {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        filter: globalFilter,
        sort: sortString,
        ...filters,
      };

      console.log('Fetch params:', params);
      const queryParams = new URLSearchParams(params).toString();
      const url = `${route(routeName)}?${queryParams}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add authorization headers if needed
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const result = await response.json();

      // Update data and recordCount
      setData(result.data || result.resumes || []);
      setRecordCount(result.recordCount || 0);

      // Call the onDataFetched callback if provided
      if (typeof onDataFetched === 'function') {
        onDataFetched(result);
      }
    } catch (error) {
      console.log(error);
      // Handle error appropriately
    } finally {
      setLoading(false);
    }
  }, [
    sorting,
    pagination.pageIndex,
    pagination.pageSize,
    globalFilter,
    routeName,
    recompute,
    filters,
  ]);

  // Fetch data when component mounts and when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 if startRow is greater than endRow
  useEffect(() => {
    if (startRow > endRow && currentPage > 1) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [startRow, endRow, currentPage]);

  return (
    <div className="relative sm:rounded-lg">
      {/* Search Input */}
      <div className="p-4">
        <input
          type="text"
          defaultValue={globalFilter}
          onChange={handleFilterChange}
          placeholder="Search..."
          className="block w-full p-2 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 rounded-md focus:outline-none focus:ring focus:border-blue-300 dark:focus:border-blue-500"
        />
      </div>

      <>
        {/* Table */}
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          {/* Table Head */}
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSorted = table.getState().sorting.find((sort) => sort.id === header.id);
                  const isSortedDesc = isSorted ? isSorted.desc : undefined;
                  const headerClassName = header.column.columnDef.meta?.className || '';

                  return (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={`px-6 py-3 cursor-pointer ${headerClassName}`}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {isSorted ? (isSortedDesc ? ' 🔽' : ' 🔼') : null}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          {/* Table Body */}
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="odd:bg-white odd:dark:bg-gray-900 even:bg-gray-50 even:dark:bg-gray-800 border-b dark:border-gray-700"
              >
                {row.getVisibleCells().map((cell) => {
                  const cellClassName = cell.column.columnDef.meta?.className || '';

                  return (
                    <td key={cell.id} className={`px-6 py-4 ${cellClassName}`}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {loading && (
          <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-50 dark:bg-opacity-50 flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <LoadingSpinner />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading...</span>
            </div>
          </div>
        )}
        {/* Pagination */}
        <nav className="p-3 flex items-center justify-between" aria-label="Table navigation">
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
            Showing{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {startRow}-{endRow}
            </span>{' '}
            of <span className="font-semibold text-gray-900 dark:text-white">{totalRows}</span>
          </span>

          {totalPages > 1 && (
            <div className="flex items-center space-x-1">
              {/* Previous Button */}
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              >
                Previous
              </button>

              {/* Page Numbers or Dropdown */}
              {totalPages <= 15 ? (
                // Render page numbers as buttons for small number of pages
                <ul className="flex items-center space-x-1 text-sm h-8">
                  {Array.from({ length: totalPages }, (_, index) => (
                    <li key={index}>
                      <button
                        onClick={() => table.setPageIndex(index)}
                        className={`flex items-center justify-center px-3 h-8 leading-tight border border-gray-300 rounded-md hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white ${
                          currentPage === index + 1
                            ? 'text-blue-600 bg-blue-50 dark:bg-gray-700 dark:text-white'
                            : 'text-gray-500 bg-white'
                        }`}
                      >
                        {index + 1}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                // Simple page number input with total page count
                <div className="flex items-center space-x-1">
                  <div className="flex items-center">
                    <input
                      type="number"
                      min="1"
                      max={totalPages}
                      value={currentPage}
                      onChange={(e) => {
                        const page = parseInt(e.target.value, 10);
                        if (page >= 1 && page <= totalPages) {
                          table.setPageIndex(page - 1);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const page = parseInt(e.target.value, 10);
                          if (page >= 1 && page <= totalPages) {
                            table.setPageIndex(page - 1);
                          }
                        }
                      }}
                      className="h-8 w-24 px-2 text-center border border-gray-300 rounded-md bg-white text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                    />
                    <span className="ml-1 text-gray-500 dark:text-gray-400">of {totalPages}</span>
                  </div>
                </div>
              )}

              {/* Next Button */}
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              >
                Next
              </button>
            </div>
          )}
        </nav>
      </>
    </div>
  );
};

export default DataTableServerSide;
